import { Session } from "@supabase/auth-helpers-react";
import { Navigate, RouteObject, useLocation } from "react-router-dom";
import AuthContainer from "./components/auth/auth-container";
import AppLayout from "./components/layout/app-layout";
import GroupChatLayout from "./components/layout/group-chat-layout";
import MainLayout from "./components/layout/main";
import ThemeExampleCard from "./components/shared/theme/ThemeExampleCard";

import LoginRoute from "./components/auth/login-route";
import AdminOnly from "./components/auth/AdminOnly";
import { GroupChatContextWrapper } from "./hooks/GroupChatContextWrapper";
import AcceptInvitePage from "./pages/accept-invite-page";
import AccessRequestGrantPage from "./pages/access-request-grant-page";
import AccessRequestRejectPage from "./pages/access-request-reject-page";
import AccountPage from "./pages/account-page";
import AdminAllChatsPage from "./pages/admin-all-chats-page";
import AdminAssistantVoicePage from "./pages/admin-assistant-voice-page";
import AdminCallsPage from "./pages/admin-calls-page";
import AdminContentPage from "./pages/admin-content-page";
import AdminDbPage from "./pages/admin-db-page";
import AdminInboundDocumentJobsPage from "./pages/admin-inbound-document-jobs-page";
import AdminArticleOutlineDetailPage from "./pages/admin-article-outline-detail-page";
import AdminArticleDetailPage from "./pages/admin-article-detail-page";
import AdminArticleAiPolicyPage from "./pages/admin-article-ai-policy-page";
import AdminArticleReviewDetailPage from "./pages/admin-article-review-detail-page";
import AdminMedicalConceptDetailPage from "./pages/admin-medical-concept-detail-page";
import AdminArticleReviewsByArticlePage from "./pages/admin-article-reviews-by-article-page";
import AdminMessageTemplatesPage from "./pages/admin-message-templates-page";
import AdminMedicationOrderHarnessPage from "./pages/admin-medication-order-harness-page";
import AdminTemplateCreatePage from "./pages/admin-template-create-page";
import AdminTemplateEditPage from "./pages/admin-template-edit-page";
import AdminTemplatesPage from "./pages/admin-templates-page";
import { AdminPage } from "./pages/admin-page";
import AdminUserChatPage from "./pages/admin-user-chat";
import ThemePage from "./pages/theme-page";
import AmbientConversationsPage from "./pages/ambient-conversations-page";
import ClaimAccountPage from "./pages/claim-account-page";
import ComponentsShowcase from "./pages/components-showcase";
import { ContactsPage } from "./pages/contacts-page";
import ContentListPage from "./pages/content-list-page";
import ContentPage from "./pages/content-page";
import CreateContactPage from "./pages/create-contact-page";
import DataDeletionRequestPage from "./pages/data-deletion-request-page";
import DeepLinkPage from "./pages/deep-link-page";
import DiagnosticTestPage from "./pages/diagnostic-test-page";
import DownloadAppPage from "./pages/download-app-page";
import FileStorePage from "./pages/file-store-page";
import ForgotPasswordPage from "./pages/forgot-password-page";
import HomePage from "./pages/home-page";
import IdentityVerifyPage from "./pages/identity-verify-page";
import InviteUserPage from "./pages/invite-user-page";
import MedicalRecordDetailPage from "./pages/medical-record-detail-page";
import MedicalRecordRequestDetailPage from "./pages/medical-record-request-detail-page";
import MedicalRecordsAdminRequestsPage from "./pages/medical-records-admin-requests-page";
import MedicalRecordsPage from "./pages/medical-records-page";
import MedicalRecordsRequestsPage from "./pages/medical-records-requests-page";
import PasswordRecoveryPage from "./pages/password-recovery-page";
import RequestRecordsPage from "./pages/request-records-page";
import RunStepsPage from "./pages/run-steps-page";
import VerifyPhonePage from "./pages/verify-phone-page";
import MediaDetailPage from "./features/media/MediaDetailPage";

import ContactViewPage from "./pages/contact-view-page";
import GroupsChatPageList from "./pages/group-chat-list-page";
import UpdateContactPage from "./pages/update-contact-page";

// Create a protected route wrapper component that checks for authentication
const ProtectedRoute = ({
  children,
  session,
}: {
  children: React.ReactNode;
  session: Session | null;
}) => {
  const location = useLocation();

  if (!session) {
    // Redirect to the login page with a redirect param and save the current location in state
    const fullPath = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        to={{
          pathname: "/login",
          search: `?redirect=${encodeURIComponent(fullPath)}`,
        }}
        state={{ from: location }}
        replace
      />
    );
  }

  return <>{children}</>;
};

// Define routes with authentication requirements
export const createRoutes = (session: Session | null): RouteObject[] => [
  {
    path: "/download-app",
    element: <DownloadAppPage />,
  },
  {
    path: "/",
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: session ? <HomePage /> : <AuthContainer />,
      },
      {
        path: "request-records/:slug",
        element: <RequestRecordsPage />,
      },
      {
        path: "chat",
        element: (
          <ProtectedRoute session={session}>
            <MainLayout />
          </ProtectedRoute>
        ),
      },
      {
        path: "contacts",
        element: (
          <ProtectedRoute session={session}>
            <ContactsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "contacts/create",
        element: (
          <ProtectedRoute session={session}>
            <CreateContactPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "contacts/update/:relationshipId",
        element: (
          <ProtectedRoute session={session}>
            <UpdateContactPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "contacts/invite",
        element: (
          <ProtectedRoute session={session}>
            <InviteUserPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "contacts/invite/:relationshipId",
        element: (
          <ProtectedRoute session={session}>
            <InviteUserPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "contacts/:relationshipId",
        element: (
          <ProtectedRoute session={session}>
            <ContactViewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "ambient",
        element: (
          <ProtectedRoute session={session}>
            <AmbientConversationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "files",
        element: (
          <ProtectedRoute session={session}>
            <FileStorePage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records/section/:section",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records/user/:userId",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records/user/:userId/section/:section",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records/user/:userId/:recordType/:recordId",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records/:recordType/:recordId",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records/requests",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordsRequestsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records/requests/all",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordsAdminRequestsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records/requests/all/:requestId",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordRequestDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "medical-records/requests/:requestId",
        element: (
          <ProtectedRoute session={session}>
            <MedicalRecordRequestDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "group-chats",
        element: (
          <ProtectedRoute session={session}>
            <GroupsChatPageList />
          </ProtectedRoute>
        ),
      },
      {
        path: "account",
        element: (
          <ProtectedRoute session={session}>
            <AccountPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "example-components",
        element: <ComponentsShowcase />,
      },
      {
        path: "admin",
        element: (
          <ProtectedRoute session={session}>
            <AdminPage themeExample={<ThemeExampleCard />} />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/assistant-voice",
        element: (
          <ProtectedRoute session={session}>
            <AdminAssistantVoicePage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/calls",
        element: (
          <ProtectedRoute session={session}>
            <AdminCallsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/inbound-document-jobs",
        element: (
          <ProtectedRoute session={session}>
            <AdminInboundDocumentJobsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/dev-tools/medication-order-harness",
        element: (
          <ProtectedRoute session={session}>
            <AdminOnly featureName="Medication Ordering Harness">
              <AdminMedicationOrderHarnessPage />
            </AdminOnly>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content",
        element: (
          <ProtectedRoute session={session}>
            <AdminContentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/outlines",
        element: (
          <ProtectedRoute session={session}>
            <AdminContentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/medical-concepts",
        element: (
          <ProtectedRoute session={session}>
            <AdminContentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/articles",
        element: (
          <ProtectedRoute session={session}>
            <AdminContentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/reviews",
        element: (
          <ProtectedRoute session={session}>
            <AdminContentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/ai-policy",
        element: (
          <ProtectedRoute session={session}>
            <AdminContentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/media",
        element: (
          <ProtectedRoute session={session}>
            <AdminContentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/outlines/:id",
        element: (
          <ProtectedRoute session={session}>
            <AdminArticleOutlineDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/articles/:slug",
        element: (
          <ProtectedRoute session={session}>
            <AdminArticleDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/reviews/:id",
        element: (
          <ProtectedRoute session={session}>
            <AdminArticleReviewDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/reviews/article/:articleId",
        element: (
          <ProtectedRoute session={session}>
            <AdminArticleReviewsByArticlePage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/medical-concepts/:id",
        element: (
          <ProtectedRoute session={session}>
            <AdminMedicalConceptDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/media/:idOrSlug",
        element: (
          <ProtectedRoute session={session}>
            <MediaDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/content/article-ai-policy",
        element: (
          <ProtectedRoute session={session}>
            <AdminArticleAiPolicyPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/message-templates",
        element: (
          <ProtectedRoute session={session}>
            <AdminMessageTemplatesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/templates",
        element: (
          <ProtectedRoute session={session}>
            <AdminTemplatesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/templates/new",
        element: (
          <ProtectedRoute session={session}>
            <AdminTemplateCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/templates/:id/edit",
        element: (
          <ProtectedRoute session={session}>
            <AdminTemplateEditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "theme",
        element: (
          <ProtectedRoute session={session}>
            <ThemePage />
          </ProtectedRoute>
        ),
      },
      {
        path: "example-components",
        element: (
          <ProtectedRoute session={session}>
            <ThemePage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/all-chats",
        element: (
          <ProtectedRoute session={session}>
            <AdminAllChatsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/all-chats/:userId",
        element: (
          <ProtectedRoute session={session}>
            <AdminUserChatPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/db",
        element: (
          <ProtectedRoute session={session}>
            <AdminDbPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "content",
        element: <ContentListPage />,
      },
      {
        path: "content/:contentId",
        element: <ContentPage />,
      },
      {
        path: "diagnostic-tests/:testId",
        element: (
          <ProtectedRoute session={session}>
            <DiagnosticTestPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "messages/:messageId/run-steps",
        element: (
          <ProtectedRoute session={session}>
            <RunStepsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "login",
        element: <LoginRoute />,
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordPage />,
      },
      {
        path: "password-recovery",
        element: <PasswordRecoveryPage />,
      },
      {
        path: "claim-account",
        element: <ClaimAccountPage />,
      },
      {
        path: "verify-phone",
        element: <VerifyPhonePage />,
      },
      {
        path: "identity/verify",
        // Public route so unauthenticated users can complete identity verification
        element: <IdentityVerifyPage />,
      },
      {
        path: "data-deletion-request",
        element: <DataDeletionRequestPage />,
      },
      {
        path: "signup",
        element: session ? (
          <Navigate to="/" replace />
        ) : (
          <AuthContainer defaultIsLoginView={false} />
        ),
      },

      {
        path: "dl/*",
        element: <DeepLinkPage />,
      },

      {
        path: "group-chat/:groupChatId",
        element: (
          <ProtectedRoute session={session}>
            <GroupChatContextWrapper>
              <GroupChatLayout />
            </GroupChatContextWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "invite/accept/:slug",
        element: <AcceptInvitePage />,
      },
      {
        path: "invite/accept",
        element: <Navigate to="/download-app" replace />,
      },
      {
        path: "access-request/grant/:accessGrantJwt",
        element: <AccessRequestGrantPage />,
      },
      {
        path: "access-request/reject/:accessRejectJwt",
        element: <AccessRequestRejectPage />,
      },
      {
        path: "*",
        element: (
          <div className="flex flex-col items-center justify-center min-h-[80vh] bg-cream">
            <h1 className="text-4xl font-bold text-terracotta mb-4">404</h1>
            <p className="text-xl text-primary-600 mb-8">Page not found</p>
            <a
              href="/"
              className="px-4 py-2 bg-cream text-terracotta rounded-lg hover:bg-beige transition-colors"
            >
              Go Home
            </a>
          </div>
        ),
      },
    ],
  },
];

export default createRoutes;
